import { environment } from "../environment";
import { stripLanguageFromPathname } from "./languages";

/**
 * The current site base ("app base") of the storefront.
 *
 * Historically the app served exactly one site, rooted at the build-time constant
 * environment.APP_BASE (/home). Self-service storefronts live under /shops/<name> instead,
 * one page tree per shop, and the SAME frontend serves all of them, so the base has to be
 * derived from the URL at runtime. Everything site-scoped hangs off this value: the root
 * page (navigation, footer, brand), the site info (locales), link composition and the
 * dynamic product/overview page lookup.
 *
 * /shops is recognised by prefix, not by a list, so a shop created by a sales colleague in
 * the Pages app works without any frontend change or deploy. Paths outside /shops fall back
 * to the legacy APP_BASE, which keeps the historical /home site working unchanged.
 */

/**
 * Derives the site base from a pathname.
 * @param {string} pathname - Browser path.
 * @param {string} [currentLanguageCode] - When given, a leading language prefix is stripped
 *   first, so /de/shops/acme resolves like /shops/acme.
 * @returns {string} The base path, e.g. /shops/acme or the legacy APP_BASE.
 */
export const deriveAppBase = (pathname, currentLanguageCode = null) => {
    let p = pathname || "/";
    if (currentLanguageCode) p = stripLanguageFromPathname(p, currentLanguageCode);

    const shops = environment.SHOPS_BASE;
    if (p === shops) return shops;
    if (p.startsWith(shops + "/")) {
        const rest = p.slice(shops.length + 1);
        const name = rest.split("/")[0];
        return name ? `${shops}/${name}` : shops;
    }

    return environment.APP_BASE;
};

// The last derived base, for consumers without router access (link composition). It is set
// by BaseLayout's effect on every location change and by getMagnoliaProps during page
// loading, so by the time links render for a page, the base of that page is current.
let currentAppBase = environment.APP_BASE;

export const setCurrentAppBase = (base) => {
    currentAppBase = base || environment.APP_BASE;
};

export const getCurrentAppBase = () => currentAppBase;
